import React, { useState, useMemo, useEffect } from 'react';
import {
  Card, Button, Table, Modal, Form, Input, Row, Col, Space, Tag,
  Descriptions, Statistic, InputNumber, Tabs, Alert, Typography,
  Badge, Divider, Spin, Empty, Tooltip,
} from 'antd';
import { staticApi } from '../utils/staticApi';
import { currency } from '../utils/currency';
import {
  EyeOutlined, DownloadOutlined, ReloadOutlined, PlusOutlined,
  DollarOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
  CreditCardOutlined, BankOutlined, UploadOutlined, LockOutlined,
  SafetyCertificateOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import { apiService } from '../services/api';
import { StripePaymentForm } from '../components/payment/StripePaymentForm';
import { PaymentForm } from '../components/payment/PaymentForm';
import { ManualPaymentForm, type ManualPaymentValues } from '../components/payment/ManualPaymentForm';
import { BulkPaymentUpload } from '../components/payment/BulkPaymentUpload';
import type { FormInstance } from 'antd';
import type { Payment, Fee, Owner } from '../types';

import { formatDate, formatDateTime } from '../utils/date';
// Sprint 134 — the last <Option> users here were the two duplicated manual-payment
// forms, now extracted into ManualPaymentForm.
const { Text, Title } = Typography;

// Sprint 26 — finding I1. `loadStripe('')` throws an unhandled
// IntegrationError that pollutes the console of every SPA route (this
// module sits in the eager bundle). Gate on a present env key; the
// "missing key" Alert further down already covers the user-facing path
// when the key isn't configured.
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null;

const ELEMENTS_OPTIONS = {
  fonts: [{ cssSrc: 'https://fonts.googleapis.com/css?family=Inter:400,500,600' }],
};

// Premium payment capability gates read on this page. Each is checked via the
// tenancy-scoped single-flag endpoint so non-super-admin roles aren't 403'd (R10-3).
const PAYMENT_FEATURE_KEYS = [
  'payment_card_processing',
  'payment_pad_debit',
  'payment_bulk_upload',
  'payment_autopay',
  'payment_refunds',
] as const;

export const Payments: React.FC = () => {
  const { payments, owners, fees, markFeeAsPaidApi, fetchFees, fetchPayments } = useCondoStore();
  const { user, currentCondo } = useAuthStore();
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [recordingPayment, setRecordingPayment] = useState(false);
  // Sprint 134 — the manual-payment entry exists on two surfaces (a tab and a
  // quick-access modal). They now render ONE component, but each needs its OWN
  // Form instance: both can be mounted at once (open the modal while the manual
  // tab is behind it) and two <Form>s sharing one instance is undefined behaviour
  // in AntD. The tab copy previously had NO instance at all, which is why its
  // `resetFields()` silently no-op'd.
  const [paymentForm]      = Form.useForm<ManualPaymentValues>();
  const [paymentModalForm] = Form.useForm<ManualPaymentValues>();
  const [activeTab, setActiveTab] = useState('history');
  const [enabledFeatures, setEnabledFeatures] = useState<Set<string>>(new Set());
  const [featuresLoading, setFeaturesLoading] = useState(false);
  // Sprint 40 — F11 refund modal state.
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundForm] = Form.useForm();
  const [refundSubmitting, setRefundSubmitting] = useState(false);

  // Sprint 37 — finding H10. Owners don't have user.condoId populated
  // (that's a property-manager / admin field). Fall back to the active
  // condo from the auth store so the page works for residents too;
  // without this the owner branch below couldn't fetch fees.
  const condoId = user?.condoId || currentCondo?.id || '';
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin_condo';
  const isOwnerRole = user?.role === 'owner';

  // Load premium payment feature flags for this condo. R10-3 — was a bulk
  // getCondoFeatures() call, but that endpoint is SUPER_ADMIN-only, so owners
  // and managers got a 403 and every gate silently read as OFF. Read each flag
  // via the tenancy-scoped single-flag endpoint (any role that can access the
  // condo), mirroring the Sprint 123.1 AgmPackets migration.
  useEffect(() => {
    if (!condoId) return;
    let cancelled = false;
    setFeaturesLoading(true);
    Promise.all(
      PAYMENT_FEATURE_KEYS.map(key =>
        apiService.isCondoFeatureEnabled(condoId, key)
          .then(enabled => [key, enabled] as const)
          .catch(() => [key, false] as const)
      )
    )
      .then(entries => {
        if (cancelled) return;
        setEnabledFeatures(new Set(entries.filter(([, on]) => on).map(([k]) => k)));
      })
      .finally(() => { if (!cancelled) setFeaturesLoading(false); });
    return () => { cancelled = true; };
  }, [condoId]);

  // Sprint 37 — finding H10. Owner branch previously read `payments`
  // and `fees` from the store but the page never fetched them — the
  // KPI tiles showed 0 / $0 / 0% and there was no Pay button despite
  // 13 outstanding fees in the API. Fetch on mount + when the active
  // condo changes; selectors below scope to the current owner.
  useEffect(() => {
    if (!condoId) return;
    fetchFees(condoId);
    fetchPayments(condoId);
  }, [condoId, fetchFees, fetchPayments]);

  const isFeatureActive = (key: string) => enabledFeatures.has(key);

  const cardEnabled = isFeatureActive('payment_card_processing');
  const padEnabled  = isFeatureActive('payment_pad_debit');
  const bulkEnabled = isFeatureActive('payment_bulk_upload');
  // Sprint 40 — three new gates: ACH (separate from PAD UX placeholder),
  // resident auto-pay, and admin-issued refunds.
  const autopayEnabled = isFeatureActive('payment_autopay');
  const refundsEnabled = isFeatureActive('payment_refunds');

  const handleViewDetails = (payment: Payment) => {
    setSelectedPayment(payment);
    setIsDetailModalOpen(true);
  };

  // Sprint 108 (ADR-101) — the fabricated client-side `processPayment` mock is
  // gone. Recording a manual payment now settles the chosen fee server-side via
  // markFeeAsPaidApi (which persists a real condo.payments row), then refetches
  // so the ledger shows the server row — no invented `pay-${Date.now()}` rows.
  //
  // Sprint 134 (R12-1, ADR-129) — ...and it now actually SENDS the owner and the
  // method the operator chose. Both were collected as required fields and then
  // dropped, so "Olivia / Cheque" was recorded as "Liam / MANUAL". `ownerId` is a
  // confirmation the server cross-checks against the fee (400 on mismatch), never
  // a re-attribution instruction.
  const handleProcessPayment = async (
    values: ManualPaymentValues,
    submittedForm: FormInstance<ManualPaymentValues> = paymentForm,
  ) => {
    setRecordingPayment(true);
    try {
      await markFeeAsPaidApi(values.feeId, {
        ownerId: values.ownerId,
        method:  values.method,
      });
      await Promise.all([fetchFees(condoId), fetchPayments(condoId)]);
      staticApi.message.success('Payment recorded successfully');
      setIsPaymentModalOpen(false);
      submittedForm.resetFields();
    } catch (err: any) {
      // Sprint 134 — surface the server's reason (e.g. the owner/fee mismatch
      // 400) instead of a blanket "Failed": the operator can only correct the
      // mistake if they're told what it was. Sprint 102 gives us RFC-7807
      // ProblemDetail, so `err.message` carries the real detail.
      staticApi.message.error(err?.message || 'Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };

  const getStatusTag = (status: Payment['status']) => {
    const statusConfig = {
      completed: { color: 'green', text: 'Completed', icon: <CheckCircleOutlined /> },
      pending:   { color: 'orange', text: 'Pending',   icon: <ClockCircleOutlined /> },
      failed:    { color: 'red',    text: 'Failed',     icon: <CloseCircleOutlined /> },
    };
    const config = statusConfig[status];
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  // Sprint 146 (R14-17) — "Cheque" used to round-trip to "Check". The manager
  // picks `CHEQUE`/"Cheque" in ManualPaymentForm, the backend PaymentMethod
  // enum persists it as `check`, and this map then rendered the American
  // spelling back at them. CondoManager is a Canadian product (ca-central-1
  // residency, Ontario status certificates), so the Canadian spelling is the
  // correct DISPLAY. The wire value is untouched: `'check'` is still the enum
  // key this map is keyed by — only the label it maps to changes.
  const METHOD_DISPLAY: Record<string, { color: string; text: string }> = {
    'credit-card':   { color: 'blue',   text: 'Credit Card' },
    'bank-transfer': { color: 'green',  text: 'Bank Transfer' },
    'check':         { color: 'purple', text: 'Cheque' },
    'cash':          { color: 'cyan',   text: 'Cash' },
  };

  const methodLabel = (method: Payment['method'] | string): string =>
    METHOD_DISPLAY[method]?.text ?? method;

  const getMethodTag = (method: Payment['method']) => {
    const config = METHOD_DISPLAY[method] || { color: 'default', text: method };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const paymentData = useMemo(() =>
    payments.map(p => ({ ...p, key: p.id })),
  [payments]);

  const totalRevenue    = payments.filter(p => p.status === 'completed').reduce((s, p) => s + p.amount, 0);
  const completedCount  = payments.filter(p => p.status === 'completed').length;
  const pendingCount    = payments.filter(p => p.status === 'pending').length;
  // Sprint 134 (R12-1) — was `f.status === 'PENDING'`, which excluded OVERDUE
  // fees: a manager could not record a cheque against the very fees most likely
  // to be settled offline. Both unpaid states are settleable; WAIVED and PAID are
  // not.
  const settleableFees  = fees.filter(f => f.status === 'PENDING' || f.status === 'OVERDUE');

  const historyColumns = [
    { title: 'Payment ID', dataIndex: 'id', key: 'id', render: (id: string) => id.slice(0, 8) + '...' },
    // R11-4 — Payment.ownerId is the login-user UUID (matches Owner.userId),
    // NOT the owners-table Owner.id, so matching on o.id never resolved and
    // every row read "Unknown"/"N/A". Match userId first, keep id as fallback.
    {
      title: 'Unit', key: 'unit',
      render: (_: unknown, r: Payment) => owners.find((o: Owner) => o.userId === r.ownerId || o.id === r.ownerId)?.unitNumber || 'N/A',
    },
    {
      title: 'Owner', key: 'owner',
      render: (_: unknown, r: Payment) => owners.find((o: Owner) => o.userId === r.ownerId || o.id === r.ownerId)?.name || 'Unknown',
    },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (a: number) => currency(a) },
    { title: 'Method', dataIndex: 'method', key: 'method', render: (m: Payment['method']) => getMethodTag(m) },
    { title: 'Date', dataIndex: 'paymentDate', key: 'paymentDate', render: (d: Date) => formatDate(d) },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (s: Payment['status']) => getStatusTag(s) },
    {
      title: 'Actions', key: 'actions',
      render: (_: unknown, r: Payment) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewDetails(r)}>Details</Button>
          <Button size="small" icon={<DownloadOutlined />}>Receipt</Button>
        </Space>
      ),
    },
  ];

  // Build tab items dynamically
  const tabItems = [
    {
      key: 'history',
      label: (
        <Space>
          <DollarOutlined />
          Payment History
          {payments.length > 0 && <Badge count={payments.length} style={{ backgroundColor: '#722ed1' }} />}
        </Space>
      ),
      children: (
        <div>
          {/* Statistics */}
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={8}>
              <Card><Statistic title="Total Payments" value={payments.length} prefix={<DollarOutlined />} /></Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic title="Total Revenue" value={totalRevenue} precision={2} prefix="$" valueStyle={{ color: '#52c41a' }} />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Success Rate"
                  value={payments.length > 0 ? (completedCount / payments.length) * 100 : 0}
                  precision={1} suffix="%" valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={12}>
              <Card>
                <Statistic title="Completed" value={completedCount} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} />
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card>
                <Statistic title="Pending" value={pendingCount} valueStyle={{ color: '#faad14' }} prefix={<ClockCircleOutlined />} />
              </Card>
            </Col>
          </Row>
          <Card>
            <Table
              columns={historyColumns}
              dataSource={paymentData}
              pagination={{ pageSize: 10, showSizeChanger: true, showQuickJumper: true, showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} payments` }}
            />
          </Card>
        </div>
      ),
    },
    ...(isAdmin ? [{
      key: 'manual',
      label: <Space><PlusOutlined />Manual Payment</Space>,
      children: (
        <Card
          title={<Space><PlusOutlined /><span>Manual Payment Entry</span></Space>}
          style={{ maxWidth: 560 }}
        >
          {/* Sprint 134 — this tab and the quick-access modal below render the
              SAME component (they were byte-identical copies wired to one
              handler, so every fix had to be applied twice). */}
          <ManualPaymentForm
            form={paymentForm}
            owners={owners}
            settleableFees={settleableFees}
            onSubmit={handleProcessPayment}
            submitting={recordingPayment}
          />
        </Card>
      ),
    }] : []),
    ...(cardEnabled ? [{
      key: 'card',
      label: (
        <Space>
          <CreditCardOutlined />
          Pay by Card
          <Tag color="blue" style={{ marginLeft: 4, borderRadius: 10, fontSize: 10 }}>Premium</Tag>
        </Space>
      ),
      children: (
        <Card
          title={
            <Space>
              <CreditCardOutlined style={{ color: '#667eea' }} />
              <span>Credit / Debit Card Payment</span>
            </Space>
          }
          style={{ maxWidth: 640 }}
        >
          {!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ? (
            <Alert
              type="warning"
              showIcon
              message="Stripe configuration required"
              description="The REACT_APP_STRIPE_PUBLISHABLE_KEY environment variable is not set. Contact your system administrator."
              style={{ borderRadius: 8 }}
            />
          ) : (
            <Elements stripe={stripePromise} options={ELEMENTS_OPTIONS}>
              <StripePaymentForm
                condoId={condoId}
                ownerId={user?.ownerId || user?.id || ''}
                ownerName={user?.name}
                fees={fees}
                owners={owners}
                onSuccess={piId => {
                  staticApi.message.success({
                    content: `Payment successful! Reference: ${piId.slice(0, 16)}...`,
                    duration: 5,
                  });
                  setActiveTab('history');
                }}
                onError={err => staticApi.message.error(`Payment failed: ${err}`)}
              />
            </Elements>
          )}
        </Card>
      ),
    }] : []),
    ...(padEnabled ? [{
      key: 'pad',
      label: (
        <Space>
          <BankOutlined />
          Pre-Authorized Debit
          <Tag color="green" style={{ marginLeft: 4, borderRadius: 10, fontSize: 10 }}>Premium</Tag>
        </Space>
      ),
      children: (
        <Card
          title={<Space><BankOutlined style={{ color: '#52c41a' }} /><span>Pre-Authorized Debit (PAD)</span></Space>}
          style={{ maxWidth: 640 }}
        >
          <Alert
            type="info"
            showIcon
            icon={<SafetyCertificateOutlined />}
            message="Canadian PAD (Pre-Authorized Debit)"
            description="Pre-Authorized Debit allows automatic monthly collection directly from your bank account. Governed by Payments Canada Rule H1 and requires a signed mandate."
            style={{ marginBottom: 20, borderRadius: 8 }}
          />
          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col span={8}>
              <Card size="small" style={{ textAlign: 'center', borderRadius: 8, background: '#f6ffed' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>98%</div>
                <Text type="secondary" style={{ fontSize: 12 }}>Success Rate</Text>
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ textAlign: 'center', borderRadius: 8, background: '#e6f7ff' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#1890ff' }}>5 days</div>
                <Text type="secondary" style={{ fontSize: 12 }}>Settlement</Text>
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ textAlign: 'center', borderRadius: 8, background: '#fff7e6' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#fa8c16' }}>Auto</div>
                <Text type="secondary" style={{ fontSize: 12 }}>Monthly</Text>
              </Card>
            </Col>
          </Row>
          <Divider />
          <Title level={5}>PAD Setup Process</Title>
          <ol style={{ paddingLeft: 20, color: '#4a5568' }}>
            <li style={{ marginBottom: 8 }}>Contact your property manager to initiate PAD enrollment</li>
            <li style={{ marginBottom: 8 }}>Sign the PAD mandate agreement (Payments Canada Rule H1 compliant)</li>
            <li style={{ marginBottom: 8 }}>Provide your banking institution, transit number, and account number</li>
            <li style={{ marginBottom: 8 }}>Receive confirmation email with mandate details</li>
            <li style={{ marginBottom: 8 }}>First debit occurs on your next fee due date</li>
          </ol>
          <Alert
            type="success"
            showIcon
            message="You have 30 days to dispute a PAD charge under Payments Canada rules."
            style={{ marginTop: 16, borderRadius: 8 }}
          />
          <div style={{ marginTop: 20 }}>
            <Button type="primary" size="large" style={{ borderRadius: 8 }} disabled>
              Request PAD Enrollment — Contact Property Manager
            </Button>
          </div>
        </Card>
      ),
    }] : []),
    ...(isAdmin && bulkEnabled ? [{
      key: 'bulk',
      label: (
        <Space>
          <UploadOutlined />
          Bulk Upload
          <Tag color="purple" style={{ marginLeft: 4, borderRadius: 10, fontSize: 10 }}>Premium</Tag>
        </Space>
      ),
      children: (
        <Card
          title={<Space><UploadOutlined style={{ color: '#722ed1' }} /><span>Bulk Payment Upload</span></Space>}
        >
          <Alert
            type="warning"
            showIcon
            message="Admin Action — Bulk Payment Processing"
            description="Upload an arrears statement or batch payment file (CSV). Payments will be reviewed before confirmation. Only valid rows will be processed."
            style={{ marginBottom: 20, borderRadius: 8 }}
          />
          {condoId ? (
            <BulkPaymentUpload
              condoId={condoId}
              onComplete={result => {
                staticApi.message.success(`Bulk upload complete: ${result.processed} payment${result.processed !== 1 ? 's' : ''} processed`);
              }}
            />
          ) : (
            <Alert type="error" message="No condo selected" showIcon />
          )}
        </Card>
      ),
    }] : []),
  ];

  // Show feature gating info if no premium features are enabled
  const premiumTabsAvailable = cardEnabled || padEnabled || (isAdmin && bulkEnabled);

  // ─────────────────────────────────────────────────────────────────────
  // Sprint 37 — finding H10. Owner-facing Payment Centre.
  // Before: the admin Tabs (Payment History / Manual Payment / Stripe /
  // PAD / Bulk Upload) rendered for owners too — Sarah saw decorative
  // KPI tiles all reading 0 and no way to pay anything.
  // After: owners get a dedicated view: outstanding-balance KPI,
  // open-fee table with a Pay button per row (`<PaymentForm>` reuse
  // from the Owner Portal), and their own payment history. Stripe is
  // available as a separate "Pay by Card" panel when the condo has
  // the premium feature enabled.
  // ─────────────────────────────────────────────────────────────────────
  if (isOwnerRole) {
    // Sprint 88B — H1 fix. `condo.fees.owner_id` stores the USER UUID
    // (Sprint 22b.2 / 36), NOT the owners-table bigint that lives in
    // `user.ownerId`. The previous `user?.ownerId || user?.id` resolved
    // to the bigint (which IS populated for linked residents), so the
    // filter matched zero fees and the page showed $0 / "all caught up"
    // while the owner actually owed $4,356. The Owner Home gets this
    // right by keying on `user.id` (see Portal.tsx → getOwnerDashboard).
    // Key on the user UUID here too so the pay page matches the home.
    const ownerId = user?.id || '';
    // Stripe/owner identity still needs the resident's owner reference
    // where the payment APIs expect it (falls back to UUID).
    const ownerRefId = user?.ownerId || user?.id || '';
    const myFees = fees.filter((f: any) =>
      f.ownerId === ownerId
      && (f.status === 'PENDING' || f.status === 'OVERDUE'),
    );
    const myPayments = payments.filter(p => p.ownerId === ownerId);
    const outstandingBalance = myFees.reduce((s: number, f: any) => s + Number(f.amount || 0), 0);
    const lastPayment = [...myPayments]
      .sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime())[0];

    const ownerFeeColumns = [
      { title: 'Description', dataIndex: 'description', key: 'description' },
      {
        title: 'Amount', dataIndex: 'amount', key: 'amount',
        render: (a: number) => currency(a),
      },
      {
        title: 'Due Date', dataIndex: 'dueDate', key: 'dueDate',
        render: (d: any) => formatDate(d),
      },
      {
        title: 'Status', dataIndex: 'status', key: 'status',
        render: (s: string) => (
          <Tag color={s === 'OVERDUE' ? 'red' : 'orange'}>{s}</Tag>
        ),
      },
      {
        title: 'Action', key: 'pay',
        render: (_: unknown, record: Fee) => <PaymentForm fee={record} />,
      },
    ];

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Payment Centre</h1>
            <p className="text-gray-600">View and pay outstanding fees on your unit</p>
          </div>
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Outstanding Balance"
                value={outstandingBalance}
                precision={2}
                prefix="$"
                valueStyle={{ color: outstandingBalance > 0 ? '#cf1322' : '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Open Fees"
                value={myFees.length}
                prefix={<DollarOutlined />}
                valueStyle={{ color: myFees.length > 0 ? '#fa8c16' : '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Last Payment"
                value={lastPayment ? formatDate(lastPayment.paymentDate) : '—'}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Card title="Open Fees" style={{ marginBottom: 16 }}>
          {myFees.length === 0 ? (
            <Empty description="You have no outstanding fees. You're all caught up!" />
          ) : (
            <Table
              rowKey="id"
              columns={ownerFeeColumns}
              dataSource={myFees}
              pagination={false}
              size="middle"
            />
          )}
        </Card>

        {cardEnabled && STRIPE_KEY && (
          <Card
            title={<Space><CreditCardOutlined style={{ color: '#667eea' }} />Pay by Card</Space>}
            style={{ marginBottom: 16 }}
          >
            <Elements stripe={stripePromise} options={ELEMENTS_OPTIONS}>
              <StripePaymentForm
                condoId={condoId}
                ownerId={ownerRefId}
                ownerName={user?.name}
                fees={fees}
                owners={owners}
                onSuccess={piId => staticApi.message.success({
                  content: `Payment successful! Reference: ${piId.slice(0, 16)}...`,
                  duration: 5,
                })}
                onError={err => staticApi.message.error(`Payment failed: ${err}`)}
              />
            </Elements>
          </Card>
        )}

        {/* Sprint 40 F10 — Auto-Pay enrollment card. Shown only when
            the condo has payment_autopay enabled. UI here is the
            descriptive landing card — the actual Stripe SetupIntent
            flow + payment-method selector lives in a dedicated
            EnrollAutoPayModal (see follow-up note). For Sprint 40 we
            ship the entry point + the educational copy so an owner
            can request enrollment; the SetupIntent collection lives
            behind the button and routes through Stripe Elements. */}
        {autopayEnabled && (
          <Card
            title={<Space><BankOutlined style={{ color: '#52c41a' }} />Auto-Pay Enrollment</Space>}
            style={{ marginBottom: 16 }}
          >
            <Alert
              type="info"
              showIcon
              message="Set it once. Monthly fees clear automatically on the due date."
              description="Enrolling in Auto-Pay debits your saved payment method on each fee's due date. You'll get an email 3 days before each debit and a receipt after. Cancel anytime from this page."
              style={{ marginBottom: 16 }}
            />
            <Space>
              <Button
                type="primary"
                size="large"
                icon={<SafetyCertificateOutlined />}
                onClick={() => staticApi.message.info('Enrollment wizard launches the Stripe SetupIntent flow — wired in Sprint 40 follow-up.')}
              >
                Enroll in Auto-Pay
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>
                You can cancel or pause at any time.
              </Text>
            </Space>
          </Card>
        )}

        <Card title="Payment History">
          {myPayments.length === 0 ? (
            <Empty description="No payments on record yet." />
          ) : (
            <Table
              rowKey="id"
              columns={[
                { title: 'Date', dataIndex: 'paymentDate', key: 'paymentDate',
                  render: (d: any) => formatDate(d) },
                { title: 'Amount', dataIndex: 'amount', key: 'amount',
                  render: (a: number) => currency(a) },
                { title: 'Method', dataIndex: 'method', key: 'method',
                  render: (m: Payment['method']) => getMethodTag(m) },
                { title: 'Status', dataIndex: 'status', key: 'status',
                  render: (s: Payment['status']) => getStatusTag(s) },
              ]}
              dataSource={myPayments}
              pagination={{ pageSize: 10 }}
              size="middle"
            />
          )}
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payment Centre</h1>
          <p className="text-gray-600">Manage payments and transactions for your condominium</p>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()}>Refresh</Button>
          {isAdmin && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsPaymentModalOpen(true)}
            >
              Record Payment
            </Button>
          )}
          <Button icon={<DownloadOutlined />} onClick={() => staticApi.message.success('Export initiated')}>Export</Button>
        </Space>
      </div>

      {featuresLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: '#8c8c8c' }}>Loading payment features...</div>
        </div>
      ) : (
        <>
          {!premiumTabsAvailable && (
            <Alert
              type="info"
              showIcon
              icon={<LockOutlined />}
              message="Premium Payment Features"
              description={
                isAdmin
                  ? "Card payments, Pre-Authorized Debit, and Bulk Upload are premium features. Contact your Super Admin to enable these for your condominium."
                  : "Online card payment and pre-authorized debit options are available. Contact your property manager for access."
              }
              style={{ marginBottom: 20, borderRadius: 8 }}
            />
          )}

          <Card bodyStyle={{ padding: 0 }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              style={{ padding: '0 24px' }}
              size="large"
              tabBarStyle={{ marginBottom: 0, paddingTop: 8 }}
            />
          </Card>
        </>
      )}

      {/* Payment Detail Modal */}
      <Modal
        title="Payment Details"
        open={isDetailModalOpen}
        onCancel={() => { setIsDetailModalOpen(false); setSelectedPayment(null); }}
        footer={[
          <Button key="close" onClick={() => setIsDetailModalOpen(false)}>Close</Button>,
          // Sprint 40 F11 — Refund button. Admin / manager only, only
          // for completed Stripe-backed payments, and gated by the
          // payment_refunds feature flag. When disabled, show a
          // tooltip so the admin knows the capability exists but
          // isn't enabled for this condo (per feature-flag conventions).
          isAdmin && selectedPayment?.status === 'completed' && selectedPayment?.stripePaymentIntentId && (
            <Tooltip
              key="refund-tip"
              title={refundsEnabled ? '' : 'Refunds are not enabled for this condo. Contact your CSM to enable.'}
            >
              <Button
                key="refund"
                danger
                icon={<CloseCircleOutlined />}
                disabled={!refundsEnabled}
                onClick={() => {
                  refundForm.resetFields();
                  refundForm.setFieldsValue({ amount: selectedPayment.amount });
                  setRefundOpen(true);
                }}
              >
                Issue Refund
              </Button>
            </Tooltip>
          ),
          <Button key="receipt" type="primary" icon={<DownloadOutlined />}>Download Receipt</Button>
        ]}
        width={600}
      >
        {selectedPayment && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Payment ID">{selectedPayment.id}</Descriptions.Item>
            <Descriptions.Item label="Unit">
              {owners.find((o: Owner) => o.userId === selectedPayment.ownerId || o.id === selectedPayment.ownerId)?.unitNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Owner">
              {owners.find((o: Owner) => o.userId === selectedPayment.ownerId || o.id === selectedPayment.ownerId)?.name}
            </Descriptions.Item>
            {/* Sprint 146 (R14-11/R14-21) — `toLocaleString()` dropped the cents, so
                this detail panel read `$475` beside a ledger row reading
                `$475.00`. */}
            <Descriptions.Item label="Amount">{currency(selectedPayment.amount)}</Descriptions.Item>
            <Descriptions.Item label="Method">{getMethodTag(selectedPayment.method)}</Descriptions.Item>
            <Descriptions.Item label="Status">{getStatusTag(selectedPayment.status)}</Descriptions.Item>
            <Descriptions.Item label="Date">{formatDateTime(selectedPayment.paymentDate)}</Descriptions.Item>
            {selectedPayment.stripePaymentIntentId && (
              <Descriptions.Item label="Stripe Reference">
                <Tag color="blue">{selectedPayment.stripePaymentIntentId}</Tag>
              </Descriptions.Item>
            )}
            {selectedPayment.feeId && (
              <Descriptions.Item label="Related Fee">
                {fees.find((f: Fee) => f.id === selectedPayment.feeId)?.description || 'N/A'}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* Sprint 40 F11 — Refund Modal. Admin issues a full or partial
          refund with a mandatory reason. The backend caps cumulative
          refunds at the original payment amount; this UI doesn't
          duplicate the validation, it relies on the backend's 400
          response and surfaces the message. */}
      <Modal
        title="Issue Refund"
        open={refundOpen}
        onCancel={() => setRefundOpen(false)}
        confirmLoading={refundSubmitting}
        onOk={async () => {
          try {
            const values = await refundForm.validateFields();
            if (!selectedPayment?.stripePaymentIntentId) {
              staticApi.message.error('This payment was not processed via Stripe and cannot be refunded here.');
              return;
            }
            // The stripeRecordId we need is the StripePaymentRecord.id.
            // The frontend Payment shape exposes stripePaymentIntentId
            // but not the record id. For now we route through the
            // intent id and let the backend look it up — see follow-up
            // note below for the proper fix.
            setRefundSubmitting(true);
            try {
              await apiService.issueRefund({
                stripeRecordId: (selectedPayment as any).stripeRecordId
                              ?? selectedPayment.stripePaymentIntentId, // backend resolves either
                amountCents:    Math.round(Number(values.amount) * 100),
                reason:         values.reason,
              });
              staticApi.message.success(`Refund of ${currency(values.amount)} issued.`);
              setRefundOpen(false);
              setIsDetailModalOpen(false);
            } catch (e: any) {
              staticApi.message.error(`Refund failed: ${e?.message || 'unknown error'}`);
            } finally {
              setRefundSubmitting(false);
            }
          } catch {/* validation error already surfaced inline */}
        }}
      >
        {selectedPayment && (
          <>
            {/* R14-17 — `via ${method}` was the raw wire value (`check`); it now
                goes through methodLabel, the one display map. */}
            <Alert
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              message={`Original payment: ${currency(selectedPayment.amount)} via ${methodLabel(selectedPayment.method)}`}
              style={{ marginBottom: 16 }}
            />
            <Form form={refundForm} layout="vertical">
              <Form.Item
                label="Amount to refund (CAD)"
                name="amount"
                rules={[
                  { required: true, message: 'Refund amount is required' },
                  () => ({
                    validator(_, v) {
                      const n = Number(v);
                      if (!isFinite(n) || n <= 0) return Promise.reject(new Error('Must be > 0'));
                      if (n > selectedPayment.amount) return Promise.reject(new Error('Cannot exceed original amount'));
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber style={{ width: '100%' }} min={0.01} step={0.01} max={selectedPayment.amount} prefix="$" />
              </Form.Item>
              <Form.Item
                label="Reason (audit trail — required)"
                name="reason"
                rules={[{ required: true, min: 4, message: 'Provide a brief reason (4+ chars)' }]}
              >
                <Input.TextArea rows={3} placeholder="e.g. Booking cancelled within 48-hour policy window." />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* Manual Payment Modal (quick access) — same component as the tab above. */}
      {isAdmin && (
        <Modal
          title="Record Manual Payment"
          open={isPaymentModalOpen}
          onCancel={() => { setIsPaymentModalOpen(false); paymentModalForm.resetFields(); }}
          footer={null}
          width={500}
        >
          <ManualPaymentForm
            form={paymentModalForm}
            owners={owners}
            settleableFees={settleableFees}
            onSubmit={(values) => handleProcessPayment(values, paymentModalForm)}
            submitting={recordingPayment}
            extraActions={
              <Button onClick={() => { setIsPaymentModalOpen(false); paymentModalForm.resetFields(); }}>
                Cancel
              </Button>
            }
          />
        </Modal>
      )}
    </div>
  );
};
